import { accAPI } from "..";
import {
  MonthlyIncomeBreakdownReqParams,
  MonthlyIncomeBreakdownResponse,
  MonthlyPerformanceDashboardReqParams,
  MonthlyPerformanceIncomeData,
  MonthlyPerformanceResponse,
  MonthlyPerformanceParams,
} from "./monthlyPerformanceIncomeAPI.types";

export const monthlyPerformanceIncomeAPI = accAPI.injectEndpoints({
  endpoints: (builder) => ({
    getMonthlyPerformanceIncome: builder.query<MonthlyPerformanceIncomeData, MonthlyPerformanceDashboardReqParams>({
      query: (params) => ({
        url: `/monthly-performance?Abo=${params.abo}&Aff=${params.aff}&Period=${params.period}&IsoCountryCode=${params.isoCountryCode}&Locale=${params.locale}`,
      }),
      keepUnusedDataFor: 3600,
      extraOptions: { pathKey: "monthlyDash" },
    }),
    getMonthlyIncomeBreakdown: builder.query<MonthlyIncomeBreakdownResponse, MonthlyIncomeBreakdownReqParams>({
      query: (params) => {
        const queryParams = new URLSearchParams({
          Abo: params.abo,
          Aff: params.aff,
          Period: params.period,
          Locale: params.locale,
          IsoCountryCode: params.isoCountryCode,
        });
        return `/monthly-income-breakdown?${queryParams}`;
      },
      keepUnusedDataFor: 3600,
      extraOptions: { pathKey: "monthlyIncome" },
    }),
    getPerfBonusDetails: builder.query<MonthlyPerformanceResponse, MonthlyPerformanceParams>({
      query: (params) => {
        return `/perf-bonus-details?affAbo=${params.affAbo}&period=${params.period}&locale=${params.locale}&countryCode=${params.countryCode}`;
      },
      extraOptions: { pathKey: "monthlyDash" },
    }),
  }),
});

export const { useGetMonthlyPerformanceIncomeQuery, useGetMonthlyIncomeBreakdownQuery, useGetPerfBonusDetailsQuery } =
  monthlyPerformanceIncomeAPI;

/**
 * Export differential bonus PDF - Standalone function for file download
 * This endpoint returns a binary PDF blob, not JSON, so it doesn't use RTK Query
 *
 * @param affAbo - ABO identifier in format "aff-abo"
 * @param period - Period in YYYYMM format
 * @param locale - Locale string (e.g., "en-US")
 * @param countryCode - ISO country code
 * @param content - Content payload with all translated labels and logo
 * @param baseUrl - Base URL for the API (Mashery URL)
 * @param accessToken - Bearer token for authorization
 * @param selectedBusiness - Business context header (x-amway-selected-business)
 * @param selectedPeriod - Period context header (x-amway-selected-period)
 * @param masheryDomain - Domain for x-mashery-domain header (ACC API URL)
 * @returns Promise<Blob> - PDF file blob
 */
export const exportDifferentialBonusPDF = async (
  affAbo: string,
  period: string,
  locale: string,
  countryCode: string,
  content: import("../../types/DifferentialBonusExport").ExportDifferentialBonusRequest,
  baseUrl: string,
  accessToken: string,
  selectedBusiness: string,
  selectedPeriod: string,
  masheryDomain: string,
): Promise<Blob> => {
  const params = new URLSearchParams({
    affAbo,
    period,
    locale,
    countryCode,
  });

  const response = await fetch(`${baseUrl}/differential-bonus-export?${params}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/pdf",
      Authorization: `Bearer ${accessToken}`,
      "x-mashery-domain": masheryDomain,
      "x-amway-selected-business": selectedBusiness,
      "x-amway-selected-period": selectedPeriod,
    },
    body: JSON.stringify(content),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Export failed: ${response.statusText}${errorText ? ` - ${errorText}` : ""}`);
  }

  return response.blob();
};
